/**
 * Running 12-month spend leakage chart for the Spend Leakage page.
 *
 * One bar per complete month from the monthly reports the scheduler keeps
 * (uploaded vs not-uploaded leakage stacked), with the leakage percentage
 * as a line on the right axis. A month with no report yet is drawn empty
 * rather than as zero.
 */
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Loader2 } from 'lucide-react';
import type { SpendLeakageTrendMonth } from '../../lib/api/barratt';

interface LeakageTrendChartProps {
  months: SpendLeakageTrendMonth[];
  loading: boolean;
  formatCurrency: (value: number) => string;
}

/** £1.2m / £340k axis ticks. */
function compact(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `£${(value / 1_000_000).toFixed(1)}m`;
  if (Math.abs(value) >= 1_000) return `£${Math.round(value / 1_000)}k`;
  return `£${Math.round(value)}`;
}

interface TooltipRow { name?: string; value?: number | string; color?: string; dataKey?: string | number }
interface TooltipProps { active?: boolean; label?: string | number; payload?: TooltipRow[] }

export default function LeakageTrendChart({ months, loading, formatCurrency }: LeakageTrendChartProps) {
  const hasData = months.some((m) => m.leakage_spend !== null);
  const first = months[0]?.label;
  const last = months[months.length - 1]?.label;

  const renderTooltip = ({ active, label, payload }: TooltipProps) => {
    if (!active || !payload?.length) return null;
    const month = months.find((m) => m.label === String(label));
    if (!month || month.leakage_spend === null) {
      return <div className="rounded border bg-background p-2 text-xs shadow">{label}: no report yet</div>;
    }
    if (!month.total_spend) {
      return <div className="rounded border bg-background p-2 text-xs shadow">{label}: no invoiced spend recorded</div>;
    }
    return (
      <div className="rounded border bg-background p-2 text-xs shadow space-y-0.5">
        <div className="font-medium">{label}</div>
        <div>Leakage {formatCurrency(month.leakage_spend)} ({month.leakage_percentage ?? 0}% of {formatCurrency(month.total_spend)})</div>
        {month.untracked_leakage ? (
          <div>Upload tracking not yet running that month</div>
        ) : (
          <>
            <div>Uploaded suppliers {formatCurrency(month.uploaded_leakage ?? 0)}</div>
            <div>Not uploaded {formatCurrency(month.not_uploaded_leakage ?? 0)}</div>
          </>
        )}
      </div>
    );
  };

  return (
    <Card data-testid="leakage-trend">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          Spend leakage · running 12 months{first && last ? ` (${first} – ${last})` : ''}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Monthly leakage split by whether the supplier uploaded that month (grey = before upload tracking began); line shows leakage as a share of spend.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex h-64 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading trend…
          </div>
        ) : !hasData ? (
          <div className="flex h-64 items-center justify-center text-sm text-muted-foreground" data-testid="leakage-trend-empty">
            No monthly reports yet. They are generated on the 12th of each month; run the backfill to fill the last 12 months.
          </div>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={months} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="money" tickFormatter={compact} tick={{ fontSize: 11 }} width={64} />
                <YAxis yAxisId="pct" orientation="right" domain={[0, 100]} tickFormatter={(v: number) => `${v}%`} tick={{ fontSize: 11 }} width={44} />
                <Tooltip content={renderTooltip} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="money" dataKey="uploaded_leakage" name="Leakage · uploaded suppliers" stackId="leak" fill="#2563eb" />
                <Bar yAxisId="money" dataKey="not_uploaded_leakage" name="Leakage · not uploaded" stackId="leak" fill="#dc2626" />
                <Bar yAxisId="money" dataKey="untracked_leakage" name="Leakage · upload not tracked" stackId="leak" fill="#9ca3af" />
                <Line yAxisId="pct" type="monotone" dataKey="leakage_percentage" name="Leakage % of spend" stroke="#111827" strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
